import React, { useMemo } from 'react';
import dynamic from 'next/dynamic';

import { Button, ButtonProps, IconProps } from '@chakra-ui/react';

import { publicEnv } from '~/features/environment/public';
import { usePLPRedesignExperiment } from '~/features/experiments/ab_plp_redesign_2025/hooks';
import { TemplateButton as TemplateButtonProps } from '~/features/page-template/models/template-button';
import { useBaseUrl } from '~/hooks/use-base-url';
import { useCatalogPath } from '~/hooks/use-catalog-path';

const ArrowRightIcon = dynamic<IconProps>(() => import('@udacity/chakra-uds-icons').then((mod) => mod.ArrowRightIcon));
const ArrowRightIconVariantB = dynamic<IconProps>(() =>
  import('~/features/experiments/ab_plp_redesign_2025/icons').then((mod) => mod.ArrowRightIcon)
);

export function TemplateButton(
  templateButtonProps: TemplateButtonProps & {
    withArrowIcon?: boolean;
    ariaLabel?: string;
    openInNewTab?: boolean;
  } & ButtonProps
) {
  const {
    buttonAction,
    buttonColorScheme,
    buttonVariant,
    buttonLabel,
    customUrl,
    withArrowIcon = false,
    openInNewTab = false,
    ariaLabel,
    ...buttonProps
  } = templateButtonProps;
  const isPLPRedesignExperiment = usePLPRedesignExperiment() === 'variant_b';
  const baseUrl = useBaseUrl();
  const catalogPath = useCatalogPath();

  const isSignUpAction = buttonAction === 'Route To Sign Up';

  const linkHref = useMemo(() => {
    if (isSignUpAction) {
      return `${publicEnv.NEXT_PUBLIC_AUTH_URL}/sign-up`;
    } else if (buttonAction === 'Route To Catalog') {
      return catalogPath;
    } else if (buttonAction === 'Custom URL') {
      return customUrl;
    }
  }, [isSignUpAction, buttonAction, customUrl, catalogPath]);

  return (
    <Button
      {...(linkHref && !isSignUpAction ? { as: 'a', href: linkHref } : { as: 'button' })}
      {...(withArrowIcon && {
        rightIcon: isPLPRedesignExperiment ? (
          <ArrowRightIconVariantB height='20px' width='20px' />
        ) : (
          <ArrowRightIcon h='24px' w='24px' />
        ),
        size: 'icon-right',
      })}
      {...(ariaLabel && { 'aria-label': ariaLabel })}
      {...(openInNewTab && { target: '_blank' })}
      colorScheme={buttonColorScheme}
      variant={buttonVariant}
      width={{ base: '100%', md: 'fit-content' }}
      {...buttonProps}
      {...(isSignUpAction && {
        onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
          buttonProps?.onClick?.(e);
          const signUpUrl = `${publicEnv.NEXT_PUBLIC_AUTH_URL}/sign-up?next=${baseUrl}`;
          if (e.metaKey || e.ctrlKey || openInNewTab) {
            window.open(signUpUrl, '_blank');
            return;
          }
          window.location.href = signUpUrl;
        },
      })}
    >
      {buttonLabel}
    </Button>
  );
}
